/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Flex,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { BoxInIcon, DeleteIcon } from '@tonic-one/react-icons';
import { useLayoutEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { QuarantineApi } from '@Apis';
// import TextWithEmbeddingComponents from '../../../../components/Common/TextWithEmbeddingComponents';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { exportFile, withEditPermission } from '../../../utils/common';
import {
  base64ToString,
  checkQuarantineItemActionEnabled,
  getCustomizedName,
  getQuarantineDeleteActionURL,
  getQuarantineDownloadActionURL,
  getQuarantineRestoreActionURL,
} from '../utils/fns';
import { QuarantineItemModalName, QuarantineItemModalState } from './interface';
import { QuarantineLogTypeEnum } from '../../../types/log';
import {
  QuarantineDeleteActionReqDto,
  QuarantineDownloadActionReqDto,
} from '../utils/types';
import {
  QUARANTINE_TYPE_NAME_MAP,
  allowedFileStatusQuarantineDownload,
} from '../utils/constants';

//just for exchange online and exchange inline
export function MailPreviewContent({
  quarantineType,
  modalState,
  setModalState,
  setTrigger,
}: {
  quarantineType: QuarantineLogTypeEnum;
  modalState: QuarantineItemModalState;
  setModalState: ({ name, quarantineItems }: QuarantineItemModalState) => void;
  setTrigger: () => void;
}) {
  if (modalState.name !== QuarantineItemModalName.ContentPreview) {
    return <></>;
  }
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const { permissions } = usePermissionStore();
  const isQuarantineEditable = withEditPermission(
    PermissionNameEnum.quarantine,
    permissions
  );
  const {
    digestBlobPath,
    traceId,
    quarantineTypeKey,
    fileStatusKey,
    timestamp,
  } = modalState.quarantineItems[0];
  //MailPreview modal use one flag to check btn status
  const buttonEnable = checkQuarantineItemActionEnabled(
    quarantineTypeKey,
    fileStatusKey
  );
  const [isLoading, setIsLoading] = useState(false);
  const [mailContent, setMailContent] = useState<string>('');
  useLayoutEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const data = await QuarantineApi.apiGetMailPreviewContent(
          digestBlobPath,
          traceId
        );
        setMailContent(data as unknown as string);
      } catch {
        console.error('apiGetMailPreviewContent error');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  //just for exchange online
  const onClickDelete = async () => {
    const data: QuarantineDeleteActionReqDto = {
      itemList: [] as any[],
    };
    const url = getQuarantineDeleteActionURL(quarantineType) as
      | 'exchangeOnline/delete'
      | 'gmail/delete';
    const item = modalState.quarantineItems[0];
    try {
      if (url === 'exchangeOnline/delete') {
        data.itemList.push({
          msgReceivedTime: item.msgReceivedTime,
          uniqueId: item.uniqueId,
          uniqueLogId: item.uniqueLogId,
          emailAddress: item.violator,
          traceId: item.traceId,
          index: item.index,
          type: item.type,
          id: item.id,
          violator: item.violator,
          companyGuid: item.companyGuid,
          protectionMode: item.protectionMode,
        });
      } else if (url === 'gmail/delete') {
        data.itemList.push({
          logDocId: item.id,
          logId: item.uniqueLogId,
          logIndexId: item.index,
          logType: item.type,
          uniqueId: item.uniqueId,
          violator: item.violator,
          messageId: item.messageId,
          companyGuid: item.companyGuid,
          dedicatedMailbox: item.msgDedicatedQuarantineMailbox,
        });
      }
      await QuarantineApi.apiDeleteQuarantineLogs(url, data);
      window.showNotification({
        titleMsg: t('quarantine.modal.action.successInfo'),
        severity: 'success',
      });
      setModalState({
        name: QuarantineItemModalName.None,
        quarantineItems: [],
      });
      setTrigger();
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };

  //for exchange online and exchange inline
  const onClickRestore = async () => {
    const data: QuarantineDeleteActionReqDto = {
      itemList: [] as any[],
    };
    const url = getQuarantineRestoreActionURL(quarantineType) as
      | 'exchangeOnline/restore'
      | 'inlineMail/restore'
      | 'gmail/restore';
    const item = modalState.quarantineItems[0];
    try {
      if (url === 'exchangeOnline/restore') {
        data.itemList.push({
          msgReceivedTime: item.msgReceivedTime,
          uniqueId: item.uniqueId,
          uniqueLogId: item.uniqueLogId,
          emailAddress: item.violator,
          traceId: item.traceId,
          index: item.index,
          type: item.type,
          id: item.id,
          violator: item.violator,
          companyGuid: item.companyGuid,
          protectionMode: item.protectionMode,
        });
      } else if (url === 'inlineMail/restore') {
        data.itemList.push({
          companyGuid: item.companyGuid,
          esLogIndex: item.index,
          esLogId: item.id,
          esLogType: item.type,
          uniqueLogId: item.uniqueLogId,
          emsTraceId: item.emsTraceId,
          uniqueId: item.uniqueId,
          traceId: item.traceId,
          emailAddress: item.violator,
          recipients: item.msgTo,
          originalRecipients: item.originalMsgTo,
          backupBlobPath: item.backupBlobPath ? item.backupBlobPath : '',
          restoreBy: '',
          direction: item.mailDirectionKey,
          blobPath: item.blobPath ? item.blobPath : '',
        });
        data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(
          quarantineType
        ) as string;
      } else if (url === 'gmail/restore') {
        data.itemList.push({
          logDocId: item.id,
          logId: item.uniqueLogId,
          logIndexId: item.index,
          logType: item.type,
          uniqueId: item.uniqueId,
          violator: item.violator,
          messageId: item.messageId,
          companyGuid: item.companyGuid,
          dedicatedMailbox: item.msgDedicatedQuarantineMailbox,
        });
      }
      await QuarantineApi.apiRestoreQuarantineLogs(url, data);
      window.showNotification({
        titleMsg: t('quarantine.modal.action.successInfo'),
        severity: 'success',
      });
      setModalState({
        name: QuarantineItemModalName.None,
        quarantineItems: [],
      });
      setTrigger();
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };

  //no crypto
  //for exchange online and exchange inline
  const onClickDownload = async () => {
    const data: QuarantineDownloadActionReqDto = {
      itemList: [],
    };
    const url = getQuarantineDownloadActionURL(quarantineType) as
      | 'exchangeOnline/download'
      | 'inlineMail/download'
      | 'gmail/download';
    const item = modalState.quarantineItems[0];
    if (url === 'exchangeOnline/download') {
      if (item.fileStatusKey === 'Quarantined') {
        data.itemList.push({
          companyGuid: item.companyGuid,
          emailAddress: item.violator,
          traceId: item.traceId,
          uniqueId: item.uniqueId,
          uniqueLogId: item.uniqueLogId,
          violator: item.violator || '',
        });
      }
      data.password = '';
    } else if (url === 'inlineMail/download') {
      if (item.fileStatusKey === 'Quarantined') {
        data.itemList.push({
          uniqueId: item.uniqueId,
          backupBlobPath: item.backupBlobPath,
          blobPath: item.blobPath || '',
          companyGuid: item.companyGuid,
          deliveryDateTime: item.msgDeliveryTime,
          direction: item.mailDirectionKey,
          emailAddress: item.violator,
          esLogId: item.id,
          esLogIndex: item.index,
          esLogType: item.type,
          fileName: item.msgSubject,
          traceId: item.traceId,
          uniqueLogId: item.uniqueLogId,
          violator: item.violator,
        });
      }
      data.password = '';
      data.serviceName = QUARANTINE_TYPE_NAME_MAP.get(quarantineType) as string;
    } else if (url === 'gmail/download') {
      if (item.fileStatusKey === 'Quarantined') {
        data.itemList.push({
          logDocId: item.id,
          logId: item.uniqueLogId,
          logIndexId: item.index,
          logType: item.type, //email_address
          uniqueId: item.uniqueId,
          violator: item.violator || '',
          messageId: item.messageId,
          companyGuid: item.companyGuid,
          dedicatedMailbox: item.msgDedicatedQuarantineMailbox,
        });
      }
    }
    try {
      const res = await QuarantineApi.apiDownloadQuarantineLogs(url, data);
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const downloadInfo = JSON.parse(
          base64ToString(res.headers['download-info'])
        );
        if (downloadInfo.name !== null && downloadInfo.violator !== null) {
          const fileName = getCustomizedName(
            downloadInfo.name,
            downloadInfo.violator
          );
          exportFile(fileName, res.data as any, 'application/octet-stream');
        }
      }
      setModalState({
        name: QuarantineItemModalName.None,
        quarantineItems: [],
      });
    } catch {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  };

  const onClose = () => {
    setModalState({
      name: QuarantineItemModalName.None,
      quarantineItems: [],
    });
  };

  return (
    <Modal
      closeOnEsc
      isClosable
      isOpen={modalState.name === QuarantineItemModalName.ContentPreview}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('quarantine.modal.previewEmail.title')}</ModalHeader>
        <ModalBody>
          <Text mb="4x">
            {t('quarantine.modal.previewEmail.timestamp')} : {timestamp}
          </Text>
          <Trans i18nKey="quarantine.modal.previewEmail.download">
            placeholder
            <Link
              disabled={
                !buttonEnable ||
                !isQuarantineEditable ||
                !allowedFileStatusQuarantineDownload.includes(fileStatusKey)
              }
              pt="1px"
              pl="4px"
              onClick={onClickDownload}
            >
              placeholder
            </Link>
            placeholder
          </Trans>
          <Flex py="8px" gap="8px">
            <Button
              disabled={!buttonEnable || !isQuarantineEditable}
              variant="secondary"
              size="md"
              onClick={onClickRestore}
            >
              <Icon as={BoxInIcon} />
              {t('common.button.restore')}
            </Button>
            {(quarantineType === QuarantineLogTypeEnum.Exchange ||
              quarantineType === QuarantineLogTypeEnum.Gmail) && (
              <Button
                disabled={!buttonEnable || !isQuarantineEditable}
                variant="secondary"
                size="md"
                onClick={onClickDelete}
              >
                <Icon as={DeleteIcon} />
                {t('common.button.delete')}
              </Button>
            )}
          </Flex>
          {isLoading ? (
            <Spinner m="auto" />
          ) : (
            <Text
              width="100%"
              overflow="hide"
              wordBreak="break-all"
              backgroundColor={colorStyle.background.tertiary}
              p="10px"
            >
              {mailContent}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
